import React, { useState, useEffect, useContext, useRef, useLayoutEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  CardMedia,
  CardActions,
  Button,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  Description as DocumentIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Apartment as ApartmentIcon,
  CalendarToday,
  Delete as DeleteIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import EditCertificateDialog from './EditCertificateDialog';
import CertificateListRow from './CertificateListRow';
import axios from 'axios';
import config from '../config';
import { UTILITY_COLORS } from '../theme';
import { certificateFileName } from '../certificateFileName';

// Shared expand/collapse motion values — AnimatePresence + real height animation replaces MUI's
// Collapse for the revealed content (old gridSpan+opacity+setTimeout fake removed). The card's
// own width resize (column <-> full-width) is a plain CSS `width` transition, not Framer Motion
// `layout`/FLIP — that used a scale() transform under the hood, which visibly distorted text.
const CONTENT_VARIANTS = {
  collapsed: { height: 0, opacity: 0 },
  expanded: { height: 'auto', opacity: 1 },
};

// Define interfaces
interface Certificate {
  id: string;
  filename?: string;
  certificate_type: string;
  certificate_type_category?: string;
  issue_date: string;
  expiry_date: string;
  status?: string;
  address?: string;
  property_address: string;
  normalized_address?: string;
  street_address?: string;
  flat_number?: string[] | string;
  zipcode?: string;
  inspector_name?: string;
  certificate_number?: string;
  additional_details?: string;
  notes?: string;
  extracted_data?: { [key: string]: any };
  created_at?: string;
}

interface Flat {
  flat_number: string;
  certificates: Certificate[];
}

interface Property {
  address: string;
  flats: Flat[];
  certificates?: Certificate[];
}

interface NewCertificatesDashboardProps {
  refreshTrigger?: number;
  onCertificateClick: (certificateId: string) => void;
  onUploadSuccess?: () => void;
  showUpload?: boolean;
  setShowUpload?: (show: boolean) => void;
  pendingUploadCertificates?: any[];
  setPendingUploadCertificates?: (certs: any[]) => void;
  uploadDialogOpen?: boolean;
  setUploadDialogOpen?: (open: boolean) => void;
  uploadMatchCount?: number;
  setUploadMatchCount?: (count: number) => void;
  hasVisitedRenewals?: boolean;
  setHasVisitedRenewals?: (visited: boolean) => void;
  newlyAddedCertIds?: string[];
  setNewlyAddedCertIds?: (ids: string[]) => void;
  autoExpandCertIds?: string[];
  collapseAllTrigger?: number;
  mockCertificates?: Certificate[];
}



// Sort unit keys for tab order: "Whole Property" first if present, then flats in ascending
// numeric order (string sort would put "Flat 10" before "Flat 2").
const sortUnitKeys = (keys: string[]): string[] => {
  const wholeProperty = keys.filter(k => k === 'Whole Property');
  const rest = keys.filter(k => k !== 'Whole Property').sort((a, b) => {
    const na = parseInt(a.replace(/\D/g, ''), 10);
    const nb = parseInt(b.replace(/\D/g, ''), 10);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return a.localeCompare(b);
  });
  return [...wholeProperty, ...rest];
};

// Property Card
const PropertyCard: React.FC<{ 
  propertyData: any; 
  address: string; 
  onView: (id: string) => void;
  onDownload: (id: string) => void;
  onEdit: (certificate: Certificate) => void;
  onDelete: (id: string) => void;
  selectedCertificates: Set<string>;
  onCertificateSelection: (certificateId: string, selected: boolean) => void;
  onReminderSuccess: (message: string) => void;
  onReminderError: (error: string) => void;
  reminderRefreshTrigger?: number;
  newlyAddedCertIds?: string[];
  autoExpandCertIds?: string[];
  collapseAllTrigger?: number;
  onFieldUpdate?: () => void;
}> = ({ propertyData, address, onView, onDownload, onEdit, onDelete, selectedCertificates, onCertificateSelection, onReminderSuccess, onReminderError, reminderRefreshTrigger = 0, newlyAddedCertIds = [], autoExpandCertIds = [], collapseAllTrigger = 0, onFieldUpdate }) => {
  const { palette } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [hasBeenExpanded, setHasBeenExpanded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogMsg, setDeleteDialogMsg] = useState('');
  const pendingDeleteIds = useRef<string[]>([]);
  // Per-property preference — larger certificate rows (mainly so the thumbnail reads better),
  // toggled via the zoom +/- icons next to the delete button.
  const [enlarged, setEnlarged] = useState(false);

  const flats = propertyData.flats;
  const unitKeys = sortUnitKeys(Object.keys(flats));
  const [selectedUnit, setSelectedUnit] = useState<string>(unitKeys[0]);

  // Keep selectedUnit valid if the property's units change (e.g. a flat's last cert is deleted)
  useEffect(() => {
    if (!unitKeys.includes(selectedUnit)) {
      setSelectedUnit(unitKeys[0]);
    }
  }, [unitKeys.join('|')]);

  // Animate height across tab switches (e.g. "Whole Property" with 5 certs -> "Flat 1" with 1)
  // and across the enlarge/normal-size toggle, instead of an instant snap. This wraps the tab
  // row + content panel TOGETHER (not just the panel) and animates THAT single element's height
  // directly — the outer property card's own Framer Motion `height: 'auto'` wrapper (from
  // expand/collapse) only actively tracks size during its own mount/unmount transition; relying
  // on it to keep hugging a child that resizes independently, well after that transition
  // settled, was the source of the outer card visibly lagging behind the inner content on
  // shrink. Animating one element under our own explicit control sidesteps that entirely. No
  // Framer Motion `layout`/FLIP here either — that scale-transforms content and would distort
  // the certificate rows, same as the width-resize distortion fixed earlier this session.
  const tabSectionRef = useRef<HTMLDivElement>(null);
  const freezeHeight = () => {
    const el = tabSectionRef.current;
    if (el) el.style.height = `${el.scrollHeight}px`;
  };
  const handleSelectUnit = (unitKey: string) => {
    freezeHeight();
    setSelectedUnit(unitKey);
  };
  const handleSetEnlarged = (value: boolean) => {
    freezeHeight();
    setEnlarged(value);
  };
  useLayoutEffect(() => {
    const el = tabSectionRef.current;
    if (!el) return;
    const newHeight = el.scrollHeight;
    const raf = requestAnimationFrame(() => {
      el.style.height = `${newHeight}px`;
    });
    const settle = setTimeout(() => { el.style.height = 'auto'; }, 350);
    return () => { cancelAnimationFrame(raf); clearTimeout(settle); };
  }, [selectedUnit, enlarged]);

  // Auto-expand and auto-select the tab of any unit containing a certificate that should be
  // auto-expanded (e.g. newly added), per the confirmed "yes" auto-select behavior.
  useEffect(() => {
    if (autoExpandCertIds.length > 0) {
      const matchingUnit = Object.entries(flats).find(([, flatCerts]: [string, any]) =>
        flatCerts.some((cert: any) => autoExpandCertIds.includes(cert.id))
      );
      if (matchingUnit) {
        setExpanded(true);
        setSelectedUnit(matchingUnit[0]);
      }
    }
  }, [autoExpandCertIds, propertyData]);

  // Collapse when clear is triggered
  useEffect(() => {
    if (collapseAllTrigger > 0) setExpanded(false);
  }, [collapseAllTrigger]);
  
  // Check if this property has any newly added certificates
  const hasNewlyAddedCert = (): boolean => {
    if (hasBeenExpanded) return false; // Don't show green if already expanded
    const flats = propertyData.flats;
    return Object.values(flats).some((flatCerts: any) => 
      flatCerts.some((cert: any) => newlyAddedCertIds.includes(cert.id))
    );
  };
  
  // Auto-hide green border after 5 seconds
  useEffect(() => {
    if (hasNewlyAddedCert()) {
      const timer = setTimeout(() => {
        setHasBeenExpanded(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [hasNewlyAddedCert()]);
  
  // Handle expansion and mark as seen
  const handleToggleExpand = () => {
    setExpanded(!expanded);
    if (!expanded && hasNewlyAddedCert()) {
      setHasBeenExpanded(true);
    }
  };
  
  // Get all certificate IDs for this property
  const getAllPropertyCertificateIds = (): string[] => {
    const flats = propertyData.flats;
    const ids: string[] = [];
    Object.values(flats).forEach((flatCerts: any) => {
      flatCerts.forEach((cert: any) => {
        ids.push(cert.id);
      });
    });
    return ids;
  };
  
  // Handle delete action — open custom confirm dialog
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const propertyCertIds = getAllPropertyCertificateIds();
    const selectedCertIds = Array.from(selectedCertificates).filter(id => propertyCertIds.includes(id));
    const certIdsToDelete = selectedCertIds.length > 0 ? selectedCertIds : propertyCertIds;

    pendingDeleteIds.current = certIdsToDelete;
    setDeleteDialogMsg(
      selectedCertIds.length > 0
        ? `${selectedCertIds.length} selected certificate${selectedCertIds.length === 1 ? '' : 's'}`
        : `all ${propertyCertIds.length} certificate${propertyCertIds.length === 1 ? '' : 's'}`
    );
    setDeleteDialogOpen(true);
  };

  const handleConfirmPropertyDelete = async () => {
    setDeleteDialogOpen(false);
    for (const id of pendingDeleteIds.current) {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) continue;
        const response = await fetch(`${config.apiUrl}/api/certificates/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (response.status === 409) {
          // Cert has reminders — user already confirmed the property delete, so force it
          await fetch(`${config.apiUrl}/api/certificates/${id}?force=true`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
        } else if (!response.ok) {
          console.error(`Failed to delete certificate ${id}:`, await response.text());
        }
      } catch (error: any) {
        console.error(`Failed to delete certificate ${id}:`, error);
      }
    }
    window.location.reload();
  };
  
  const totalCertificates = Object.values(flats).reduce((total: number, flatCerts: any) => total + flatCerts.length, 0);
  
  // Determine if this property has multiple flats or is a whole property
  const getPropertyTypeIcon = () => {
    const flatKeys = Object.keys(flats);
    const hasMultipleFlats = flatKeys.length > 1;
    const hasSpecificFlats = flatKeys.some(key => key !== 'Whole Property' && key !== '' && key !== 'N/A');
    
    if (hasMultipleFlats || hasSpecificFlats) {
      return <ApartmentIcon sx={{ fontSize: 22, color: 'primary.main' }} />;
    } else {
      return <HomeIcon sx={{ fontSize: 22, color: 'primary.main' }} />;
    }
  };
  
  let totalExpired = 0;
  let totalExpiring = 0;
  
  Object.values(flats).forEach((flatCerts: any) => {
    flatCerts.forEach((cert: any) => {
      const today = new Date();
      const expiryDate = new Date(cert.expiry_date);
      const diffTime = expiryDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) totalExpired++;
      else if (diffDays <= 30) totalExpiring++;
    });
  });
  
  const totalValid = totalCertificates - totalExpired - totalExpiring;

  return (
    // One property per row (full width) — no width resize on expand, so no FLIP/transform
    // distortion risk here at all (see CertificateListRow for the per-field inline editing).
    <Box sx={{ width: '100%' }}>
    <Card sx={{
      transition: 'box-shadow 0.3s ease, background-color 0.2s ease',
      // DESIGN.md's surface-card / surface-cream-strong tokens — no purple/lavender, per spec.
      bgcolor: expanded ? palette.tokens.surfaceCreamStrong : palette.tokens.surfaceCard,
      border: 'none',
      boxShadow: 'none',
      ...(expanded && { boxShadow: '0 6px 24px rgba(0,0,0,0.09)' }),
      ...(hasNewlyAddedCert() && {
        animation: 'pulse-green-property 5s ease-in-out 1',
        border: '2px solid rgba(76, 175, 80, 0.8)',
        boxShadow: '0 0 15px 5px rgba(76, 175, 80, 0.4)',
        '@keyframes pulse-green-property': {
          '0%, 100%': {
            boxShadow: '0 0 15px 5px rgba(76, 175, 80, 0.4)',
            transform: 'scale(1)',
          },
          '50%': {
            boxShadow: '0 0 20px 10px rgba(76, 175, 80, 0.6)',
            transform: 'scale(1.01)',
          },
        },
      }),
    }}
    data-property-card
    >
      <CardActionArea onClick={handleToggleExpand} sx={{ borderRadius: 'inherit' }}>
        <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
          {/* Single row, two groups: left = icon + name + flat count (identity); right = cert
              status chips + delete + chevron. Now that properties are full-width there's room
              for both on one line; the left group truncates the address before it ever pushes
              into the right group's space. */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' }, rowGap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: '1 1 auto' }}>
              <Box sx={{
                flexShrink: 0, width: 44, height: 44, borderRadius: '10px', bgcolor: palette.tokens.white,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(20,20,19,0.08)',
              }}>
                {getPropertyTypeIcon()}
              </Box>
              {/* displaySm already maps to h4 by default (App.tsx variantMapping) — each
                  property is a subsection of the Dashboard's "Portfolio" h3, so h4 keeps the
                  page's heading order unbroken. Serif + bold matches the mockup (Portfolio's
                  own heading treatment), unlike the sans titleLg used elsewhere. */}
              <Typography
                variant="displaySm"
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 700,
                  lineHeight: 1,
                  color: palette.tokens.ink,
                  flex: '0 1 auto',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  // Cormorant Garamond's default old-style figures give street numbers varying
                  // heights/descenders ("404" looks stylized) — force uniform lining figures.
                  fontVariantNumeric: 'lining-nums',
                }}
              >
                {address}
              </Typography>
              {(() => {
                const flatKeys = Object.keys(flats).filter(k => k !== 'Whole Property' && k !== '' && k !== 'N/A');
                return flatKeys.length > 0 ? (
                  <Chip
                    label={`${flatKeys.length} ${flatKeys.length === 1 ? 'Flat' : 'Flats'}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.72rem', '& .MuiChip-label': { px: 0.75 }, borderColor: palette.tokens.mutedSoft, color: palette.tokens.muted, flexShrink: 0, ml: 1 }}
                  />
                ) : null;
              })()}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
              <Chip
                label={`${totalCertificates} Certs`}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.72rem', '& .MuiChip-label': { px: 0.75 } }}
              />
              {totalExpired > 0 && (
                <Chip
                  label={`${totalExpired} Expired`}
                  size="small"
                  color="error"
                  sx={{ height: 22, fontSize: '0.72rem', '& .MuiChip-label': { px: 0.75 } }}
                />
              )}
              {totalExpiring > 0 && (
                <Chip
                  label={`${totalExpiring} Expiring`}
                  size="small"
                  color="warning"
                  sx={{ height: 22, fontSize: '0.72rem', '& .MuiChip-label': { px: 0.75 } }}
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                <Tooltip title="Larger certificate cards">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleSetEnlarged(true); }}
                    sx={{ p: 0.5, color: enlarged ? 'primary.main' : 'action.active', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                  >
                    <ZoomInIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Normal size">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleSetEnlarged(false); }}
                    sx={{ p: 0.5, color: !enlarged ? 'primary.main' : 'action.active', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                  >
                    <ZoomOutIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={selectedCertificates.size > 0 && getAllPropertyCertificateIds().some(id => selectedCertificates.has(id)) ? "Delete selected certificates" : "Delete all certificates for this property"}>
                  <IconButton
                    size="small"
                    onClick={handleDeleteClick}
                    sx={{ p: 0.5, color: 'error.main', '&:hover': { bgcolor: 'rgba(211,47,47,0.08)' } }}
                  >
                    <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                  </IconButton>
                </Tooltip>
                <Box sx={{ color: 'action.active' }}>
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
      
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="content"
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            variants={CONTENT_VARIANTS}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <CardContent sx={{ pt: 0, px: 2.5, pb: 2.5, borderTop: `2px solid ${palette.tokens.hairline}` }}>
              <Box sx={{ mb: 2, mt: 2 }} />

              {/* Wraps the tab row + content panel together — this is the element whose height
                  we explicitly animate on tab switch (see handleSelectUnit/useLayoutEffect
                  above), so the whole block (including the tan/coral card boundary around it)
                  resizes in one synchronized motion instead of the inner panel and outer card
                  moving at different speeds. */}
              <Box
                ref={tabSectionRef}
                sx={{ overflow: 'hidden', transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)' }}
              >
              {/* Tab row — one tab per unit, status dot computed from that unit's own certs */}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0 }}>
                {unitKeys.map((unitKey) => {
                  const unitCerts = flats[unitKey] as any[];
                  const unitExpired = unitCerts.some((cert: any) => new Date(cert.expiry_date) < new Date());
                  const unitExpiring = !unitExpired && unitCerts.some((cert: any) => {
                    const diffDays = Math.ceil((new Date(cert.expiry_date).getTime() - Date.now()) / 86400000);
                    return diffDays >= 0 && diffDays <= 30;
                  });
                  const dotColor = unitExpired ? palette.tokens.error : unitExpiring ? palette.tokens.warning : palette.tokens.success;
                  const isSelected = unitKey === selectedUnit;
                  return (
                    <Box
                      key={unitKey}
                      onClick={(e) => { e.stopPropagation(); handleSelectUnit(unitKey); }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '10px 10px 0 0',
                        bgcolor: isSelected ? palette.tokens.white : 'transparent',
                        border: isSelected ? `1px solid ${palette.tokens.hairline}` : '1px solid transparent',
                        borderBottom: isSelected ? `1px solid ${palette.tokens.white}` : 'none',
                        mb: isSelected ? '-1px' : 0,
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: isSelected ? 1 : 0,
                        transition: 'background-color 0.15s ease',
                        '&:hover': !isSelected ? { bgcolor: 'rgba(255,255,255,0.5)' } : {},
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? palette.tokens.ink : palette.tokens.muted, whiteSpace: 'nowrap' }}>
                        {unitKey}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Content panel — only the selected unit's certificates, in list-row format */}
              <Box sx={{ bgcolor: palette.tokens.white, border: `1px solid ${palette.tokens.hairline}`, borderRadius: '0 8px 8px 8px', p: 2 }}>
                {(() => {
                  const unitCerts = (flats[selectedUnit] || []) as any[];
                  const unitExpiredCount = unitCerts.filter((cert: any) => new Date(cert.expiry_date) < new Date()).length;
                  const unitExpiringCount = unitCerts.filter((cert: any) => {
                    const diffDays = Math.ceil((new Date(cert.expiry_date).getTime() - Date.now()) / 86400000);
                    return diffDays >= 0 && diffDays <= 30;
                  }).length;
                  const sortByExpiry = (a: any, b: any) => {
                    const priority = (cert: any) => {
                      const d = Math.ceil((new Date(cert.expiry_date).getTime() - Date.now()) / 86400000);
                      return d < 0 ? 1 : d <= 30 ? 2 : 3;
                    };
                    const pa = priority(a), pb = priority(b);
                    return pa !== pb ? pa - pb : new Date(a.expiry_date).getTime() - new Date(b.expiry_date).getTime();
                  };
                  return (
                    <>
                      <Typography sx={{ fontSize: '0.8rem', color: palette.tokens.muted, mb: 1.5 }}>
                        {unitCerts.length} certificate{unitCerts.length === 1 ? '' : 's'} total
                        {unitExpiredCount > 0 && ` · ${unitExpiredCount} expired`}
                        {unitExpiringCount > 0 && ` · ${unitExpiringCount} expiring soon`}
                      </Typography>
                      {unitCerts.sort(sortByExpiry).map((certificate: any) => (
                        <CertificateListRow
                          key={certificate.id}
                          certificate={certificate}
                          onView={onView}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          onReminderSuccess={onReminderSuccess}
                          onReminderError={onReminderError}
                          reminderRefreshTrigger={reminderRefreshTrigger}
                          isNewlyAdded={newlyAddedCertIds.includes(certificate.id)}
                          isAutoExpanded={autoExpandCertIds.includes(certificate.id)}
                          isDimmed={autoExpandCertIds.length > 0 && !autoExpandCertIds.includes(certificate.id)}
                          onFieldUpdate={onFieldUpdate}
                          enlarged={enlarged}
                        />
                      ))}
                    </>
                  );
                })()}
              </Box>
              </Box>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Property delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px', p: 1, maxWidth: 400, width: '100%' } }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: UTILITY_COLORS.errorSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <DeleteIcon sx={{ fontSize: 26, color: palette.tokens.error }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '17px', color: palette.tokens.ink, mb: 1 }}>
            Delete property?
          </Typography>
          <Typography sx={{ fontSize: '14px', color: palette.tokens.muted, lineHeight: 1.6 }}>
            This will permanently delete <strong>{deleteDialogMsg}</strong> for <strong>{address.split(',')[0]}</strong>. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Box
            component="button"
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ flex: 1, py: 1.25, borderRadius: '10px', border: `1px solid ${palette.tokens.hairline}`, backgroundColor: palette.tokens.white, fontSize: '14px', fontWeight: 600, color: palette.tokens.body, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { backgroundColor: palette.tokens.canvas } }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            onClick={handleConfirmPropertyDelete}
            sx={{ flex: 1, py: 1.25, borderRadius: '10px', border: 'none', backgroundColor: palette.tokens.error, fontSize: '14px', fontWeight: 600, color: palette.tokens.onPrimary, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { backgroundColor: palette.tokens.error } }}
          >
            Delete
          </Box>
        </DialogActions>
      </Dialog>
    </Card>
    </Box>
  );
};

// Main Dashboard Component
const NewCertificatesDashboard: React.FC<NewCertificatesDashboardProps> = ({ 
  refreshTrigger, 
  onCertificateClick, 
  onUploadSuccess,
  showUpload = false,
  setShowUpload,
  pendingUploadCertificates,
  setPendingUploadCertificates,
  uploadDialogOpen,
  setUploadDialogOpen,
  uploadMatchCount,
  setUploadMatchCount,
  hasVisitedRenewals,
  setHasVisitedRenewals,
  newlyAddedCertIds = [],
  setNewlyAddedCertIds,
  autoExpandCertIds: externalAutoExpandCertIds = [],
  collapseAllTrigger = 0,
  mockCertificates,
}) => {
  const [propertyTree, setPropertyTree] = useState<{ [key: string]: any }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState<Certificate | null>(null);
  const [selectedCertificates, setSelectedCertificates] = useState<Set<string>>(new Set());
  const [reminderMessage, setReminderMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const autoExpandCertIds = externalAutoExpandCertIds;
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    certificateId: string;
    message: string;
    reminders: any[];
    remindersCount: number;
  }>({
    open: false,
    certificateId: '',
    message: '',
    reminders: [],
    remindersCount: 0
  });
  const { token } = useAuth();
  const navigate = useNavigate();

  const buildTreeFromMockCerts = (certs: Certificate[]) => {
    const tree: { [key: string]: any } = {};
    for (const cert of certs) {
      const addr = cert.normalized_address || cert.property_address || 'Unknown';
      if (!tree[addr]) {
        tree[addr] = { address: addr, flats: {}, has_only_whole_property: true };
      }
      const flats = Array.isArray(cert.flat_number) && cert.flat_number.length > 0
        ? cert.flat_number
        : ['Whole Property'];
      if (flats[0] !== 'Whole Property') tree[addr].has_only_whole_property = false;
      for (const flat of flats) {
        if (!tree[addr].flats[flat]) tree[addr].flats[flat] = [];
        tree[addr].flats[flat].push(cert);
      }
    }
    return tree;
  };

  const fetchPropertyTree = async (silent = false) => {
    if (mockCertificates !== undefined) return;
    if (!token) return;

    try {
      if (!silent) setLoading(true);
      const response = await axios.get(`${config.apiUrl}/api/property-tree`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Check if response is successful
      if (response.data.success === false) {
        throw new Error('Failed to load property tree');
      }
      
      // Backend returns tree_data array, convert to object keyed by address
      const treeData = response.data.tree_data || [];
      console.log('Received tree data:', treeData);
      console.log('Response data:', response.data);
      const treeObject: any = {};
      
      // Handle empty data gracefully
      if (Array.isArray(treeData) && treeData.length > 0) {
        treeData.forEach((property: any) => {
          treeObject[property.label] = {
            address: property.label,
            street_address: property.street_address,
            zipcode: property.zipcode,
            flats: {}
          };
          
          // Check if this property has ONLY whole property certificates (no specific flats)
          // In this case, display them flat without the "Whole Property" label
          if (property.has_only_whole_property && property.whole_property_certificates) {
            // Create a pseudo-flat entry so it shows up as a "Whole Property" tab
            treeObject[property.label].flats['Whole Property'] = property.whole_property_certificates;
            treeObject[property.label].has_only_whole_property = true;
          }
          
          // Convert children array to flats object - check if children exists
          if (property.children && Array.isArray(property.children)) {
            property.children.forEach((flat: any) => {
              treeObject[property.label].flats[flat.label] = flat.certificates || [];
              // Mark if this is the "Whole Property" section in a mixed structure
              if (flat.is_whole_property) {
                treeObject[property.label].flats[flat.label]._isWholeProperty = true;
              }
            });
          }
        });
      }
      
      console.log('Converted tree object:', treeObject);
      setPropertyTree(treeObject);
      setError(null); // Clear any previous errors - empty data is not an error
    } catch (error: any) {
      console.error('Error fetching property tree:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        request: error.request,
        status: error.response?.status
      });
      
      // Only set error for actual network/server errors, not empty data
      if (error.response && error.response.status === 401) {
        setError('Authentication required. Please log in again.');
      } else if (error.response && error.response.status >= 500) {
        setError('Server error. Please try again later.');
      } else if (error.request) {
        setError('Network error. Please check your connection.');
      } else {
        setError('Failed to load certificates');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPropertyTree();
  }, [token, refreshTrigger]);

  const mockMountRef = React.useRef(true);
  useEffect(() => {
    if (mockCertificates !== undefined) {
      setPropertyTree(buildTreeFromMockCerts(mockCertificates));
      setLoading(false);
    } else if (!mockMountRef.current) {
      // mock was active and just turned off — re-fetch real data
      fetchPropertyTree();
    }
    mockMountRef.current = false;
  }, [mockCertificates]);

  const handleUploadSuccess = () => {
    fetchPropertyTree();
    if (onUploadSuccess) {
      onUploadSuccess();
    }
  };

  const handleEditCertificate = (certificate: Certificate) => {
    setEditingCertificate(certificate);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = (updatedCertificate: Certificate) => {
    // Trigger refresh to update organization
    fetchPropertyTree();
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingCertificate(null);
  };

  // Inline field edits (title/category/dates on CertificateListRow) already update themselves
  // optimistically — this just resyncs the property tree's aggregate counts/status dots in the
  // background, without the full-page spinner a normal fetchPropertyTree() would show.
  const handleInlineCertUpdate = () => {
    fetchPropertyTree(true);
  };

  const handleReminderChange = () => {
    // Refresh data when reminders change
    fetchPropertyTree();
  };

  // Certificate selection handlers
  const handleCertificateSelection = (certificateId: string, selected: boolean) => {
    setSelectedCertificates(prev => {
      const newSelection = new Set(prev);
      if (selected) {
        newSelection.add(certificateId);
      } else {
        newSelection.delete(certificateId);
      }
      return newSelection;
    });
  };

  const handleReminderSuccess = (message: string) => {
    setReminderMessage({ type: 'success', text: message });
    // Clear message after 5 seconds
    setTimeout(() => setReminderMessage(null), 5000);
    // Refresh certificate data to show updated email status
    fetchPropertyTree();
  };

  const handleReminderError = (error: string) => {
    setReminderMessage({ type: 'error', text: error });
    // Clear error after 10 seconds
    setTimeout(() => setReminderMessage(null), 10000);
  };

  // Certificate action handlers
  const handleViewCertificate = async (certificateId: string) => {
    if (!token) return;
    try {
      const response = await fetch(`${config.apiUrl}/api/certificates/${certificateId}/file`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Failed to open certificate:', error);
    }
  };

  const findCertificateById = (node: any, id: string): Certificate | null => {
    if (!node || typeof node !== 'object') return null;
    if (node.id === id && node.certificate_type) return node as Certificate;
    for (const child of Object.values(node)) {
      const found = findCertificateById(child, id);
      if (found) return found;
    }
    return null;
  };

  const handleDownloadCertificate = async (certificateId: string) => {
    try {
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      // Create download link
      const response = await fetch(`${config.apiUrl}/api/certificates/${certificateId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Named after the certificate itself; the server's header is only a fallback (it is not readable on cross-origin redirects)
        const cert = findCertificateById(propertyTree, certificateId);
        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = cert
          ? certificateFileName(cert)
          : contentDisposition?.match(/filename="?(.+)"?/)?.[1] || `certificate-${certificateId}.pdf`;
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download certificate');
      }
    } catch (error: any) {
      console.error('Failed to download certificate');
    }
  };

  const handleDeleteCertificate = async (certificateId: string) => {
    // No confirmation here - it's handled by the EnhancedCertificateCard component
    
    try {
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      // Make DELETE request to backend
      const response = await fetch(`${config.apiUrl}/api/certificates/${certificateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Handle 409 Conflict - certificate has reminders
      if (response.status === 409) {
        const data = await response.json();
        setDeleteConfirmDialog({
          open: true,
          certificateId: certificateId,
          message: data.message || 'This certificate has reminders set.',
          reminders: data.reminders || [],
          remindersCount: data.reminders_count || 0
        });
        return;
      }
      
      if (response.ok) {
        console.log(`Certificate ${certificateId} deleted successfully`);
        
        // Update the property tree state by removing the certificate without full refresh
        setPropertyTree(prevTree => {
          const newTree = { ...prevTree };
          
          // Find and remove the certificate from the tree
          Object.keys(newTree).forEach(address => {
            Object.keys(newTree[address].flats || {}).forEach(flatKey => {
              const certificates = newTree[address].flats[flatKey];
              if (Array.isArray(certificates)) {
                newTree[address].flats[flatKey] = certificates.filter(
                  (cert: any) => cert.id !== certificateId
                );
                
                // Remove empty flat entries
                if (newTree[address].flats[flatKey].length === 0) {
                  delete newTree[address].flats[flatKey];
                }
              }
            });
            
            // Remove address if no flats remain
            if (Object.keys(newTree[address].flats || {}).length === 0) {
              delete newTree[address];
            }
          });
          
          return newTree;
        });
      } else {
        const errorText = await response.text();
        console.error('Failed to delete certificate:', errorText);
        alert('Failed to delete certificate. Please try again.');
      }
    } catch (error: any) {
      console.error('Failed to delete certificate:', error);
      alert('Failed to delete certificate. Please try again.');
    }
  };

  // Handle confirmed deletion with force=true (deletes reminders too)
  const handleConfirmDelete = async () => {
    const certificateId = deleteConfirmDialog.certificateId;
    
    // Close the dialog first
    setDeleteConfirmDialog({
      open: false,
      certificateId: '',
      message: '',
      reminders: [],
      remindersCount: 0
    });
    
    try {
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      // Make DELETE request with force=true to delete reminders too
      const response = await fetch(`${config.apiUrl}/api/certificates/${certificateId}?force=true`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        console.log(`Certificate ${certificateId} and its reminders deleted successfully`);
        
        // Update the property tree state by removing the certificate
        setPropertyTree(prevTree => {
          const newTree = { ...prevTree };
          
          // Find and remove the certificate from the tree
          Object.keys(newTree).forEach(address => {
            Object.keys(newTree[address].flats || {}).forEach(flatKey => {
              const certificates = newTree[address].flats[flatKey];
              if (Array.isArray(certificates)) {
                newTree[address].flats[flatKey] = certificates.filter(
                  (cert: any) => cert.id !== certificateId
                );
                
                // Remove empty flat entries
                if (newTree[address].flats[flatKey].length === 0) {
                  delete newTree[address].flats[flatKey];
                }
              }
            });
            
            // Remove address if no flats remain
            if (Object.keys(newTree[address].flats || {}).length === 0) {
              delete newTree[address];
            }
          });
          
          return newTree;
        });
      } else {
        const errorText = await response.text();
        console.error('Failed to delete certificate:', errorText);
        alert('Failed to delete certificate. Please try again.');
      }
    } catch (error: any) {
      console.error('Failed to delete certificate:', error);
      alert('Failed to delete certificate. Please try again.');
    }
  };

  // Handle dialog cancellation
  const handleCancelDelete = () => {
    setDeleteConfirmDialog({
      open: false,
      certificateId: '',
      message: '',
      reminders: [],
      remindersCount: 0
    });
  };



  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const propertyCount = Object.keys(propertyTree).length;


  return (
    <Box sx={{ minHeight: '100%', height: '100%', width: '100%', overflowX: 'hidden' }}>

      {/* Success/Error Messages */}
      {reminderMessage && (
        <Alert 
          severity={reminderMessage.type} 
          sx={{ mb: 3 }} 
          onClose={() => setReminderMessage(null)}
        >
          {reminderMessage.text}
        </Alert>
      )}




      {/* Properties — one per row (full width) */}
      {propertyCount > 0 && (
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'flex-start',
        }}>
          {Object.entries(propertyTree).map(([address, propertyData]) => (
            <PropertyCard 
              key={address}
              propertyData={propertyData}
              address={address}
              onView={handleViewCertificate}
              onDownload={handleDownloadCertificate}
              onEdit={handleEditCertificate}
              onDelete={handleDeleteCertificate}
              selectedCertificates={selectedCertificates}
              onCertificateSelection={handleCertificateSelection}
              onReminderSuccess={handleReminderSuccess}
              onReminderError={handleReminderError}
              newlyAddedCertIds={newlyAddedCertIds}
              autoExpandCertIds={autoExpandCertIds}
              collapseAllTrigger={collapseAllTrigger}
              onFieldUpdate={handleInlineCertUpdate}
            />
          ))}
        </Box>
      )}

      {/* Edit Certificate Dialog */}
      <EditCertificateDialog
        open={editDialogOpen}
        certificate={editingCertificate}
        onClose={handleCloseEditDialog}
        onSave={handleSaveEdit}
      />

      {/* Delete Confirmation Dialog (for certificates with reminders) */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCancelDelete}
        aria-labelledby="delete-confirmation-dialog-title"
        aria-describedby="delete-confirmation-dialog-description"
      >
        <DialogTitle id="delete-confirmation-dialog-title">
          Delete Certificate with Reminders?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-confirmation-dialog-description">
            {deleteConfirmDialog.message}
          </DialogContentText>
          {deleteConfirmDialog.remindersCount > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Active Reminders:</strong> {deleteConfirmDialog.remindersCount}
              </Typography>
              {deleteConfirmDialog.reminders.length > 0 && (
                <Box sx={{ mt: 1, pl: 2 }}>
                  {deleteConfirmDialog.reminders.slice(0, 3).map((reminder: any, index: number) => (
                    <Typography key={index} variant="caption" color="text.secondary" display="block">
                      • {new Date(reminder.reminder_date).toLocaleDateString()} - Status: {reminder.status}
                    </Typography>
                  ))}
                  {deleteConfirmDialog.reminders.length > 3 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      ... and {deleteConfirmDialog.reminders.length - 3} more
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            Deleting this certificate will also delete all associated reminders and scheduled emails.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            Delete Certificate & Reminders
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewCertificatesDashboard;